import { AnimatePresence, motion } from "framer-motion";
import { Plane, Sparkles, Wind } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const routeProfiles = {
  home: {
    trailStart: "rgba(15, 111, 255, 0.02)",
    trailMid: "rgba(15, 111, 255, 0.62)",
    trailEnd: "rgba(247, 179, 43, 0.95)",
    glow: "rgba(15, 111, 255, 0.28)",
    welcome: "Come on, explore your best-fit study destination."
  },
  study: {
    trailStart: "rgba(74, 106, 255, 0.02)",
    trailMid: "rgba(54, 108, 255, 0.56)",
    trailEnd: "rgba(247, 179, 43, 0.84)",
    glow: "rgba(74, 106, 255, 0.26)",
    welcome: "Compare top universities and shortlist with confidence."
  },
  about: {
    trailStart: "rgba(16, 156, 255, 0.02)",
    trailMid: "rgba(16, 156, 255, 0.58)",
    trailEnd: "rgba(247, 179, 43, 0.86)",
    glow: "rgba(16, 156, 255, 0.28)",
    welcome: "Meet the mentors behind your global journey."
  },
  testimonials: {
    trailStart: "rgba(53, 105, 255, 0.02)",
    trailMid: "rgba(53, 105, 255, 0.6)",
    trailEnd: "rgba(247, 179, 43, 0.85)",
    glow: "rgba(53, 105, 255, 0.28)",
    welcome: "Read real student outcomes before you decide."
  },
  contact: {
    trailStart: "rgba(17, 132, 218, 0.02)",
    trailMid: "rgba(17, 132, 218, 0.56)",
    trailEnd: "rgba(247, 179, 43, 0.88)",
    glow: "rgba(17, 132, 218, 0.28)",
    welcome: "Drop your details and our team will guide you quickly."
  },
  default: {
    trailStart: "rgba(15, 111, 255, 0.02)",
    trailMid: "rgba(15, 111, 255, 0.56)",
    trailEnd: "rgba(247, 179, 43, 0.82)",
    glow: "rgba(15, 111, 255, 0.26)",
    welcome: "Your next step is one click away."
  }
};

const TRAIL_POINT_LIMIT = 30;
const ROTATION_LIMIT = 46;

function resolveProfile(pathname) {
  if (pathname === "/") return routeProfiles.home;
  if (pathname.startsWith("/study-in/")) return routeProfiles.study;
  if (pathname.startsWith("/about")) return routeProfiles.about;
  if (pathname.startsWith("/testimonials")) return routeProfiles.testimonials;
  if (pathname.startsWith("/contact")) return routeProfiles.contact;
  return routeProfiles.default;
}

function resolveMessage(rawText, pathname) {
  const text = (rawText || "").trim().toLowerCase();

  if (!text || text.length < 3) return null;
  if (text.includes("book")) return "Come on, lock in your free counselling slot.";
  if (text.includes("contact") || text.includes("talk") || text.includes("call")) {
    return "Talk to us now and get a clear study roadmap.";
  }
  if (text.includes("connect")) return "Connect now and let us guide your next move.";
  if (text.includes("explore") || text.includes("destination") || text.includes("study in")) {
    return "Come on, know which destination fits your profile best.";
  }
  if (text.includes("testimonial") || text.includes("story")) {
    return "Check these stories and choose your path with confidence.";
  }
  if (text.includes("send") || text.includes("inquiry")) {
    return "Share your details and we will reach out fast.";
  }
  if (pathname.startsWith("/contact")) return "Need help right now? We are ready for you.";
  if (pathname.startsWith("/study-in/")) return "Let us match your profile to the right universities.";
  return null;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function hashStringToSeed(input) {
  let hash = 0;
  for (let index = 0; index < input.length; index += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash);
}

function seededUnit(seed, offset) {
  const value = Math.sin(seed * 12.9898 + offset * 78.233) * 43758.5453;
  return value - Math.floor(value);
}

function buildTrailPath(points) {
  if (points.length < 2) return "";

  const first = points[0];
  let path = `M ${first.x.toFixed(1)} ${first.y.toFixed(1)}`;

  for (let index = 1; index < points.length - 1; index += 1) {
    const point = points[index];
    const next = points[index + 1];
    const controlX = (point.x + next.x) / 2;
    const controlY = (point.y + next.y) / 2;
    path += ` Q ${point.x.toFixed(1)} ${point.y.toFixed(1)} ${controlX.toFixed(1)} ${controlY.toFixed(1)}`;
  }

  const last = points[points.length - 1];
  path += ` L ${last.x.toFixed(1)} ${last.y.toFixed(1)}`;
  return path;
}

function FlightAssistant() {
  const { pathname } = useLocation();
  const profile = useMemo(() => resolveProfile(pathname), [pathname]);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  const [anchor, setAnchor] = useState({ x: 120, y: 180, rotation: 0 });
  const [trailPoints, setTrailPoints] = useState([]);
  const [bubbleText, setBubbleText] = useState("");
  const [planeScale, setPlaneScale] = useState(1);
  const [bubbleAnchorRect, setBubbleAnchorRect] = useState(null);

  const anchorRef = useRef({ x: 120, y: 180, rotation: 0 });
  const lastHoverElementRef = useRef(null);
  const bubbleTimeoutRef = useRef(null);
  const boostResetRef = useRef(null);
  const hasInitializedPositionRef = useRef(false);

  const gradientId = useMemo(
    () => `flight-trail-${(pathname || "home").replace(/[^a-zA-Z0-9_-]/g, "-")}`,
    [pathname]
  );
  const trailPath = useMemo(() => buildTrailPath(trailPoints), [trailPoints]);
  const isMobile = viewport.width > 0 ? viewport.width < 640 : false;
  const mobilePattern = useMemo(() => {
    const seed = hashStringToSeed(pathname || "/");
    return {
      amp1: 18 + seededUnit(seed, 1) * 14,
      amp2: 10 + seededUnit(seed, 2) * 12,
      amp3: 6 + seededUnit(seed, 3) * 8,
      freq1: 3.6 + seededUnit(seed, 4) * 2.1,
      freq2: 7.8 + seededUnit(seed, 5) * 3.2,
      freq3: 12.2 + seededUnit(seed, 6) * 4.4,
      phase1: seededUnit(seed, 7) * Math.PI * 2,
      phase2: seededUnit(seed, 8) * Math.PI * 2,
      phase3: seededUnit(seed, 9) * Math.PI * 2,
      yWobble: 3 + seededUnit(seed, 10) * 5,
      yFreq: 8 + seededUnit(seed, 11) * 4,
      yPhase: seededUnit(seed, 12) * Math.PI * 2
    };
  }, [pathname]);
  const transformValue = `translate3d(${anchor.x}px, ${anchor.y}px, 0) rotate(${anchor.rotation}deg) scale(${planeScale})`;

  const cloudParticles = useMemo(
    () =>
      Array.from({ length: 6 }, (_, index) => ({
        id: `cloud-${index}`,
        size: 20 + (index % 3) * 14,
        left: 14 + index * 13,
        top: 12 + (index % 4) * 20,
        driftX: index % 2 === 0 ? 18 : -14,
        driftY: index % 2 === 0 ? -8 : -5,
        duration: 5.5 + index * 0.65,
        delay: index * 0.2
      })),
    []
  );

  const bubblePosition = useMemo(() => {
    const bubbleWidth = isMobile ? 250 : 320;
    const bubbleHeight = isMobile ? 126 : 148;
    const minGap = 12;
    const maxLeft = viewport.width > 0 ? viewport.width - bubbleWidth - minGap : 860;
    const maxTop = viewport.height > 0 ? viewport.height - bubbleHeight - minGap : 560;

    let left = anchor.x + 56;
    let top = anchor.y - 118;

    if (left > maxLeft) {
      left = anchor.x - bubbleWidth - 30;
    }
    if (left < minGap) {
      left = minGap;
    }
    if (top < minGap) {
      top = anchor.y + 44;
    }
    if (top > maxTop) {
      top = maxTop;
    }

    if (bubbleAnchorRect) {
      const intersectsAnchor =
        !(left + bubbleWidth < bubbleAnchorRect.left
          || left > bubbleAnchorRect.right
          || top + bubbleHeight < bubbleAnchorRect.top
          || top > bubbleAnchorRect.bottom);

      if (intersectsAnchor) {
        const above = bubbleAnchorRect.top - bubbleHeight - 14;
        const below = bubbleAnchorRect.bottom + 14;
        const rightSide = bubbleAnchorRect.right + 18;
        const leftSide = bubbleAnchorRect.left - bubbleWidth - 18;

        if (above >= minGap) {
          top = above;
        } else if (below <= maxTop) {
          top = below;
        } else if (rightSide <= maxLeft) {
          left = rightSide;
        } else if (leftSide >= minGap) {
          left = leftSide;
        }
      }
    }

    left = clamp(left, minGap, maxLeft);
    top = clamp(top, minGap, maxTop);

    const side = left < anchor.x ? "left" : "right";
    return { left, top, side };
  }, [anchor, bubbleAnchorRect, isMobile, viewport]);

  const triggerBubble = useCallback((message, duration = 2600, anchorRect = null) => {
    if (isMobile) return;
    if (!message) return;

    setBubbleText(message);
    setBubbleAnchorRect(anchorRect);
    setPlaneScale(1.12);

    if (boostResetRef.current) {
      window.clearTimeout(boostResetRef.current);
    }
    boostResetRef.current = window.setTimeout(() => {
      setPlaneScale(1);
    }, 260);

    if (bubbleTimeoutRef.current) {
      window.clearTimeout(bubbleTimeoutRef.current);
    }
    bubbleTimeoutRef.current = window.setTimeout(() => {
      setBubbleText("");
      setBubbleAnchorRect(null);
      lastHoverElementRef.current = null;
    }, duration);
  }, [isMobile]);

  useEffect(() => {
    const updateViewport = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setViewport((previous) => (
        previous.width === width && previous.height === height
          ? previous
          : { width, height }
      ));

      if (!hasInitializedPositionRef.current) {
        const initial = {
          x: clamp(width * 0.16, 40, width - 80),
          y: clamp(height * 0.32, 68, height - 88),
          rotation: 0
        };
        anchorRef.current = initial;
        setAnchor(initial);
        setTrailPoints([initial]);
        hasInitializedPositionRef.current = true;
      }
    };

    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  useEffect(() => {
    setTrailPoints([]);
    const base = anchorRef.current;
    setTrailPoints([base]);
  }, [pathname]);

  const setPlanePosition = useCallback((nextX, nextY, nextRotation) => {
    if (viewport.width === 0 || viewport.height === 0) return;

    const previous = anchorRef.current;
    const x = clamp(nextX, 36, Math.max(viewport.width - 80, 36));
    const y = clamp(nextY, 62, Math.max(viewport.height - 86, 62));
    const rotation = clamp(nextRotation, -ROTATION_LIMIT, ROTATION_LIMIT);
    const distance = Math.hypot(x - previous.x, y - previous.y);
    const next = { x, y, rotation };

    anchorRef.current = next;
    setAnchor(next);

    if (distance > 1.2) {
      setTrailPoints((prev) => [...prev.slice(-(TRAIL_POINT_LIMIT - 1)), { x, y }]);
    }
  }, [viewport.height, viewport.width]);

  useEffect(() => {
    if (isMobile || viewport.width === 0 || viewport.height === 0) return undefined;

    let rafId = null;

    const handleMouseMove = (event) => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const previous = anchorRef.current;
        const x = event.clientX + 14;
        const y = event.clientY - 18;
        const dx = x - previous.x;
        const dy = y - previous.y;
        const distance = Math.hypot(dx, dy);
        const rotation = distance > 0.1
          ? (Math.atan2(dy, dx) * 180) / Math.PI
          : previous.rotation;

        setPlanePosition(x, y, rotation);
        rafId = null;
      });
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isMobile, setPlanePosition, viewport.height, viewport.width]);

  useEffect(() => {
    if (!isMobile || viewport.width === 0 || viewport.height === 0) return undefined;

    let rafId = null;

    const updateScrollFlight = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const maxScroll = Math.max(
          document.documentElement.scrollHeight - window.innerHeight,
          1
        );
        const progress = Math.min(window.scrollY / maxScroll, 1);
        const centerX = viewport.width * 0.5;
        const travelWidth = Math.max(40, viewport.width * 0.34);
        const wave1 = Math.sin(progress * Math.PI * mobilePattern.freq1 + mobilePattern.phase1) * mobilePattern.amp1;
        const wave2 = Math.sin(progress * Math.PI * mobilePattern.freq2 + mobilePattern.phase2) * mobilePattern.amp2;
        const wave3 = Math.sin(progress * Math.PI * mobilePattern.freq3 + mobilePattern.phase3) * mobilePattern.amp3;
        const x = centerX + ((wave1 + wave2 + wave3) / 52) * travelWidth;

        const yTravel = Math.max(viewport.height - 154, 120);
        const y = 64
          + progress * yTravel
          + Math.sin(progress * Math.PI * mobilePattern.yFreq + mobilePattern.yPhase) * mobilePattern.yWobble;

        const previous = anchorRef.current;
        const dx = x - previous.x;
        const dy = y - previous.y;
        const distance = Math.hypot(dx, dy);
        const rotation = distance > 0.1
          ? (Math.atan2(dy, dx) * 180) / Math.PI
          : previous.rotation;

        setPlanePosition(x, y, rotation);
        rafId = null;
      });
    };

    window.addEventListener("scroll", updateScrollFlight, { passive: true });
    return () => {
      window.removeEventListener("scroll", updateScrollFlight);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isMobile, mobilePattern, setPlanePosition, viewport.height, viewport.width]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      triggerBubble(profile.welcome, 3000, null);
    }, 800);

    return () => {
      window.clearTimeout(timer);
      if (bubbleTimeoutRef.current) {
        window.clearTimeout(bubbleTimeoutRef.current);
      }
      if (boostResetRef.current) {
        window.clearTimeout(boostResetRef.current);
      }
    };
  }, [profile, triggerBubble]);

  useEffect(() => {
    if (!isMobile) return;
    setBubbleText("");
    setBubbleAnchorRect(null);
  }, [isMobile]);

  useEffect(() => {
    const triggerHoverMessage = (target) => {
      if (!(target instanceof Element)) return;
      const clickable = target.closest("a,button");
      if (!clickable || clickable === lastHoverElementRef.current) return;
      if (clickable.closest("[aria-hidden='true']")) return;

      const text = clickable.getAttribute("data-flight-message")
        || clickable.getAttribute("aria-label")
        || clickable.textContent;
      const message = resolveMessage(text, pathname);

      if (!message) return;
      lastHoverElementRef.current = clickable;
      triggerBubble(message, 2500, clickable.getBoundingClientRect());
    };

    const onMouseOver = (event) => {
      triggerHoverMessage(event.target);
    };
    const onFocusIn = (event) => {
      triggerHoverMessage(event.target);
    };
    const onTap = (event) => {
      triggerHoverMessage(event.target);
    };

    document.addEventListener("mouseover", onMouseOver);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("click", onTap, true);
    return () => {
      document.removeEventListener("mouseover", onMouseOver);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("click", onTap, true);
    };
  }, [pathname, triggerBubble]);

  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-[45] overflow-hidden">
      <svg className="absolute inset-0 h-full w-full">
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={profile.trailStart} />
            <stop offset="60%" stopColor={profile.trailMid} />
            <stop offset="100%" stopColor={profile.trailEnd} />
          </linearGradient>
        </defs>

        {trailPath ? (
          <>
            <path
              d={trailPath}
              fill="none"
              stroke={`url(#${gradientId})`}
              strokeWidth="3.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity="0.9"
            />
            <path
              d={trailPath}
              fill="none"
              stroke="rgba(255,255,255,0.34)"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="12 16"
              opacity="0.62"
            />
          </>
        ) : null}
      </svg>

      {(isMobile ? cloudParticles.slice(0, 3) : cloudParticles).map((cloud) => (
        <motion.span
          key={cloud.id}
          className="absolute rounded-full bg-white/28 blur-[1px]"
          style={{
            width: cloud.size,
            height: cloud.size * 0.56,
            left: `${cloud.left}%`,
            top: `${cloud.top}%`
          }}
          animate={{
            x: [0, cloud.driftX, 0],
            y: [0, cloud.driftY, 0],
            opacity: [0.2, 0.42, 0.2]
          }}
          transition={{
            duration: cloud.duration,
            repeat: Infinity,
            ease: "easeInOut",
            delay: cloud.delay
          }}
        />
      ))}

      <div className="absolute left-0 top-0 will-change-transform" style={{ transform: transformValue }}>
        <div className="relative h-14 w-14 sm:h-16 sm:w-16">
          <motion.span
            className="absolute -inset-0.5 rounded-full blur-md"
            style={{ backgroundColor: profile.glow }}
            animate={{ opacity: [0.35, 0.72, 0.35], scale: [0.95, 1.05, 0.95] }}
            transition={{ duration: 2.6, ease: "easeInOut", repeat: Infinity }}
          />
          <span className="absolute left-[-58px] top-1/2 h-px w-16 -translate-y-1/2 bg-gradient-to-r from-transparent to-royal-blue/42" />
          <span className="absolute left-[-34px] top-1/2 h-px w-10 -translate-y-1/2 bg-gradient-to-r from-transparent to-warm-gold/48" />
          <span className="absolute -bottom-2 left-2 h-3 w-12 rounded-full bg-midnight-navy/20 blur-md" />

          <div className="absolute left-2.5 top-2.5 inline-flex h-9 w-9 items-center justify-center rounded-[14px] border border-white/85 bg-white/95 text-royal-blue shadow-luxury sm:left-3 sm:top-3 sm:h-10 sm:w-10">
            <Plane className="h-4.5 w-4.5 sm:h-5 sm:w-5" />
          </div>

          <motion.span
            className="absolute left-2 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-warm-gold/90 blur-[1px]"
            animate={{ x: [-2, -18], opacity: [0.85, 0], scale: [1, 0.62] }}
            transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }}
          />
          <motion.span
            className="absolute left-4 top-[56%] h-2 w-2 -translate-y-1/2 rounded-full bg-royal-blue/80 blur-[1px]"
            animate={{ x: [0, -16], opacity: [0.7, 0], scale: [0.95, 0.56] }}
            transition={{ duration: 0.86, repeat: Infinity, ease: "linear", delay: 0.26 }}
          />
        </div>
      </div>

      <AnimatePresence>
        {bubbleText ? (
          <motion.div
            key={bubbleText}
            initial={{ opacity: 0, y: 8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            style={{ left: bubblePosition.left, top: bubblePosition.top }}
            className="absolute z-[70] max-w-[250px] rounded-2xl border border-white/20 bg-midnight-navy/95 px-3.5 py-2.5 text-xs font-medium text-soft-white shadow-[0_22px_56px_-24px_rgba(7,18,38,0.92)] backdrop-blur-sm sm:max-w-[320px] sm:px-4 sm:py-3 sm:text-sm"
          >
            <span className="mb-1.5 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-sky-glow/90">
              <Sparkles className="h-3 w-3" />
              Pilot Assist
            </span>
            <p className="leading-relaxed text-soft-white">{bubbleText}</p>
            {bubblePosition.side === "right" ? (
              <span className="absolute -left-1.5 top-5 h-3 w-3 rotate-45 border-b border-l border-white/20 bg-midnight-navy/95" />
            ) : null}
            {bubblePosition.side === "left" ? (
              <span className="absolute -right-1.5 top-5 h-3 w-3 rotate-45 border-r border-t border-white/20 bg-midnight-navy/95" />
            ) : null}
            <span className="absolute right-3 top-2 inline-flex text-sky-glow/60">
              <Wind className="h-3.5 w-3.5" />
            </span>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export default FlightAssistant;
